import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const timeAgo = (dateStr) => {
  if (!dateStr) return 'just now';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString();
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'submission':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      );
    case 'invoice':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'client':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
  }
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // ── 1. Fetch Notifications from Database on Mount ──
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/notifications');
      if (res.data?.data) {
        setNotifications(res.data.data);
        setUnread(res.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications from DB:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── 2. Real-time SSE Listener ──
  useEffect(() => {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    let es;
    let reconnectTimer;

    const connectSSE = () => {
      const token = localStorage.getItem('wd_token');
      if (!token) return;

      const url = `${baseURL}/api/admin/notifications/stream?token=${token}`;
      es = new EventSource(url);

      es.onmessage = (event) => {
        try {
          const newNotif = JSON.parse(event.data);
          if (newNotif && newNotif.id) {
            setNotifications((prev) => {
              if (prev.some((n) => n.id === newNotif.id)) return prev;
              return [newNotif, ...prev];
            });
            setUnread((u) => u + 1);

            // Onscreen Toast popup alert
            toast.success(`🔔 ${newNotif.title || 'New Notification'}\n${newNotif.message || ''}`, {
              duration: 5000,
              position: 'top-right',
            });

            // Native browser notification if granted
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              new Notification(newNotif.title || 'Notification', {
                body: newNotif.message || '',
                icon: '/favicon.ico',
              });
            }
          }
        } catch (e) {
          console.error('Error parsing SSE message:', e);
        }
      };

      es.onerror = () => {
        es?.close();
        reconnectTimer = setTimeout(connectSSE, 6000);
      };
    };

    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    connectSSE();

    return () => {
      es?.close();
      clearTimeout(reconnectTimer);
    };
  }, []);

  // ── Close dropdown on click outside ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOpen = () => setOpen((o) => !o);

  // ── DB Actions ──

  // Mark single notification as read in DB & State
  const markAsRead = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/api/admin/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => {
          if (n.id === id && !n.is_read) {
            setUnread((u) => Math.max(0, u - 1));
            return { ...n, is_read: true };
          }
          return n;
        })
      );
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  // Delete single notification from DB & State
  const deleteNotif = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await api.delete(`/api/admin/notifications/${id}`);
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === id);
        if (target && !target.is_read) {
          setUnread((u) => Math.max(0, u - 1));
        }
        return prev.filter((n) => n.id !== id);
      });
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  // Mark all notifications as read in DB & State
  const markAllAsRead = async () => {
    try {
      await api.patch('/api/admin/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  // Delete all notifications from DB & State
  const deleteAll = async () => {
    try {
      await api.delete('/api/admin/notifications');
      setNotifications([]);
      setUnread(0);
    } catch (err) {
      console.error('Failed to delete all notifications:', err);
    }
  };

  const handleNotifClick = useCallback(
    (notif) => {
      if (!notif.is_read) {
        markAsRead(notif.id);
      }
      setOpen(false);
      if (notif.link) {
        navigate(notif.link);
      }
    },
    [navigate]
  );

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* ── Bell Button ── */}
      <button
        onClick={toggleOpen}
        title="Notifications"
        style={{
          position: 'relative',
          background: open ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 8,
          width: 34,
          height: 34,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={unread > 0 ? 'var(--accent)' : 'currentColor'}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: 17, height: 17, color: 'var(--text-secondary)' }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Unread badge */}
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: 'var(--danger)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              borderRadius: 20,
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
              pointerEvents: 'none',
              border: '1.5px solid #0a0c10',
              boxShadow: '0 0 8px rgba(244,63,94,0.6)',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* ── Dropdown Panel ── */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 360,
            background: 'var(--bg-card)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'fadeSlideDown 0.15s ease',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                Notifications
              </span>
              {unread > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    background: 'rgba(244,63,94,0.15)',
                    color: 'var(--danger)',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontWeight: 700,
                  }}
                >
                  {unread} new
                </span>
              )}
            </div>

            {notifications.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {unread > 0 && (
                  <button
                    onClick={markAllAsRead}
                    title="Mark all as read"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--accent)',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={deleteAll}
                  title="Delete all"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    fontSize: 11,
                    padding: 0,
                  }}
                >
                  Delete all
                </button>
              </div>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: '36px 20px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 32, height: 32, margin: '0 auto 10px', display: 'block', opacity: 0.4 }}
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                No notifications yet.
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotifClick(n)}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '12px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    cursor: 'pointer',
                    background: n.is_read ? 'transparent' : 'rgba(251,191,36,0.05)',
                    transition: 'background 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = n.is_read ? 'transparent' : 'rgba(251,191,36,0.05)')
                  }
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: n.is_read ? 'rgba(255,255,255,0.05)' : 'rgba(251,191,36,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: 2,
                    }}
                  >
                    {getNotificationIcon(n.type)}
                  </div>

                  {/* Notification Content */}
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 54 }}>
                    <div
                      style={{
                        fontWeight: n.is_read ? 500 : 700,
                        fontSize: 13,
                        color: n.is_read ? 'var(--text-secondary)' : 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {n.title || 'Notification'}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-muted)',
                        marginTop: 3,
                        lineHeight: 1.4,
                      }}
                    >
                      {n.message}
                    </div>
                    {/* Timestamp moved to bottom */}
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--text-muted)',
                        marginTop: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        opacity: 0.8,
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 11, height: 11 }}>
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {timeAgo(n.created_at)}
                    </div>
                  </div>

                  {/* ── Action Buttons: Mark as Read & Delete Trash Icon ── */}
                  <div
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {/* Mark as Read Button */}
                    {!n.is_read && (
                      <button
                        onClick={(e) => markAsRead(n.id, e)}
                        title="Mark as read"
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: 'none',
                          borderRadius: 6,
                          color: 'var(--accent)',
                          padding: '5px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ width: 13, height: 13 }}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                    )}

                    {/* Trash Can Delete Button */}
                    <button
                      onClick={(e) => deleteNotif(n.id, e)}
                      title="Delete notification"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: 'none',
                        borderRadius: 6,
                        color: 'var(--text-muted)',
                        padding: '5px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--danger)';
                        e.currentTarget.style.background = 'rgba(244,63,94,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--text-muted)';
                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ width: 14, height: 14 }}
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
